import React, { useMemo } from "react";

interface TranscriptEntry {
  startRaw: string;
  endRaw: string;
  startDisplay: string;
  endDisplay: string;
  speaker: string;
  message: string;
}

interface CallTranscriptProps {
  transcriptText: string;
}

function normalizeTimestamp(raw: string): string {
  const cleaned = raw.trim().replace(",", ".");
  const match = cleaned.match(/^(\d{2}):(\d{2}):(\d{2})(?:[.,]\d{3})?$/);
  if (!match) return raw.trim();
  const [, hh, mm, ss] = match;
  return `${hh}:${mm}:${ss}`;
}

function parseVttTranscript(text: string): TranscriptEntry[] {
  if (!text) return [];

  let content = text.replace(/^\uFEFF/, "").trim();

  if (content.startsWith("WEBVTT")) {
    content = content.split(/\r?\n/).slice(1).join("\n").trim();
  }

  const lines = content.split(/\r?\n/);
  const entries: TranscriptEntry[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    const tsMatch = line.match(
      /^(\d{2}:\d{2}:\d{2}[.,]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[.,]\d{3})/
    );

    if (!tsMatch) {
      i += 1;
      continue;
    }

    const startRaw = tsMatch[1];
    const endRaw = tsMatch[2];
    i += 1;

    const messageLines: string[] = [];
    while (i < lines.length) {
      const current = lines[i];
      if (
        /^(\d{2}:\d{2}:\d{2}[.,]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[.,]\d{3})/.test(
          current.trim()
        )
      ) {
        break;
      }
      if (current.trim() === "") {
        i += 1;
        break;
      }
      messageLines.push(current);
      i += 1;
    }

    const messageJoined = messageLines.join("\n").trim();

    let speaker = "";
    let message = messageJoined;
    const speakerMatch = messageJoined.match(/^([^:\n]+):\s*([\s\S]*)$/);
    if (speakerMatch) {
      speaker = speakerMatch[1].trim();
      message = (speakerMatch[2] || "").trim();
    } else {
      speaker = "Speaker";
    }

    entries.push({
      startRaw,
      endRaw,
      startDisplay: normalizeTimestamp(startRaw),
      endDisplay: normalizeTimestamp(endRaw),
      speaker,
      message,
    });
  }

  return entries;
}

const CallTranscript: React.FC<CallTranscriptProps> = ({ transcriptText }) => {
  const entries = useMemo(
    () => parseVttTranscript(transcriptText),
    [transcriptText]
  );

  if (!entries.length) {
    return <div className="text-muted-foreground">No transcript available</div>;
  }

  return (
    <div className="p-3 md:p-4">
      <div className="space-y-3 md:space-y-4">
        {entries.map((entry, idx) => {
          const isUser = /user/i.test(entry.speaker);
          const isAssistant = /assistant|agent|system/i.test(entry.speaker);
          const alignment = isUser ? "justify-end" : "justify-start";
          const bubbleBg = isUser
            ? "bg-primary text-white"
            : "bg-muted text-foreground";
          const speakerBadge = isUser ? "text-emerald-100" : "text-muted-foreground";

          return (
            <div
              key={`${entry.startRaw}-${idx}`}
              className={`flex ${alignment}`}
            >
              <div className="max-w-[90%] md:max-w-[75%] min-w-0">
                <div className="text-[10px] leading-none text-muted-foreground/60 font-mono mb-1">
                  {entry.startDisplay} → {entry.endDisplay}
                </div>
                <div className={`rounded-lg px-3 py-2 ${bubbleBg}`}>
                  <div
                    className={`text-[11px] uppercase tracking-wide font-semibold opacity-80 mb-1 ${speakerBadge}`}
                  >
                    {isAssistant
                      ? "Assistant"
                      : isUser
                      ? "User"
                      : entry.speaker}
                  </div>
                  <div className="whitespace-pre-wrap break-words text-sm">
                    {entry.message}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CallTranscript;
